import { useMutation, useQuery, UseQueryResult } from 'react-query'
import { makeCoreDeleteRequest, makeCoreGetRequest, makeCorePatchRequest, makeCorePostRequest } from 'utils/api'
import { handleQueryError } from 'utils/helpers'
import { JobsApiResponse, JobTableItem, JobCategoryApiResponse, JobCategory, JobDetailsApiResponse } from './interfaces'

export const useJobsQuery = (
  showNotification: Function,
  getParams: string
): UseQueryResult<JobsApiResponse, Error> => {
  return useQuery<JobsApiResponse, Error>(
    ['jobs', getParams],
    async () => {
      const result = await makeCoreGetRequest(getParams)
      return result
    },
    {
      onError: error =>
        handleQueryError(error, 'There was an error fetching jobs.', showNotification)
    }
  )
}

export const useJobQuery = (
  showNotification: Function,
  jobId: number,
  shouldRefreshData: boolean
): UseQueryResult<JobTableItem, Error> => {
  return useQuery<JobTableItem, Error>(
    ['job', jobId, shouldRefreshData],
    async () => {
      const result = await makeCoreGetRequest(`/iam/management-tools/jobs/${jobId}`)
      return result
    },
    {
      onError: error =>
        handleQueryError(error, 'There was an error fetching the job.', showNotification),
      enabled: !!jobId
    }
  )
}

export const useJobCategoryQuery = (
  showNotification: Function,
  jobCatId: string,
  shouldRefreshData: boolean
): UseQueryResult<JobCategory, Error> => {
  return useQuery<JobCategory, Error>(
    ['job', jobCatId, shouldRefreshData],
    async () =>  makeCoreGetRequest(`/iam/management-tools/job-categories/${jobCatId}`),
    {
      onError: error =>
        handleQueryError(error, 'There was an error fetching the job.', showNotification),
      enabled: !!jobCatId
    }
  )
}

export const useJobCategoriesQuery = (
  showNotification: Function,
  getParams: string
): UseQueryResult<JobCategoryApiResponse, Error> => {
  return useQuery<JobCategoryApiResponse, Error>(
    ['job-categories', getParams],
    async () => {
      const result = await makeCoreGetRequest(getParams)
      return result
    },
    {
      onError: error =>
        handleQueryError(error, 'There was an error fetching job-categories.', showNotification)
    }
  )
}

export const useJobDetailsQuery = (
  showNotification: Function,
  getParams: string
): UseQueryResult<JobDetailsApiResponse, Error> => {
  return useQuery<JobDetailsApiResponse, Error>(
    ['job-details', getParams],
    async () => {
      const result = await makeCoreGetRequest(getParams)
      return result
    },
    {
      onError: error =>
        handleQueryError(error, 'There was an error fetching job details.', showNotification)
    }
  )
}


export const useUpdateJobMutation = (showNotification: Function) => {
  return useMutation<JobTableItem, Error, { jobId: number, updatedData: Partial<JobTableItem> }>(
    async ({ jobId, updatedData }) => {
      const result = await makeCorePatchRequest(`/iam/management-tools/jobs/${jobId}`, updatedData)
      return result
    },
    {
      onError: error => handleQueryError(error, 'There was an error updating the job.', showNotification),
      onSuccess: () => showNotification({ message: 'Job updated successfully', type: 'success' })
    }
  )
}

export const useUpdateJobCategoryMutation = (showNotification: Function) => {
  return useMutation<JobCategory, Error, { jobCatId: number; updatedData: Partial<JobCategory> }>(
    async ({ jobCatId, updatedData }) => {
      const result = await makeCorePatchRequest(`/iam/management-tools/job-categories/${jobCatId}`, updatedData)
      return result
    },
    {
      onError: error => handleQueryError(error, 'There was an error updating the job categories.', showNotification),
      onSuccess: () => showNotification({ message: 'Job categories updated successfully', type: 'success' })
    }
  )
}

export const useDeleteJobMutation = (showNotification: Function) => {
  return useMutation<void, Error, string>(
    async (jobId) => {
      await makeCoreDeleteRequest(`/api/jobs/${jobId}`, {})
    },
    {
    onError: error => handleQueryError(error, 'There was an error deleting the job.', showNotification),
    onSuccess: () => showNotification({ message: 'Job deactivated successfully', type: 'success' })
    }
  )
}

export const useCreateJobMutation = (showNotification: Function) => {
  return useMutation<JobTableItem, Error, Partial<JobTableItem>>(
    async (newJobData) => {
      const result = await makeCorePostRequest('/iam/management-tools/jobs', newJobData)
      return result
    },
    {
      onError: error => handleQueryError(error, 'There was an error creating the job.', showNotification),
      onSuccess: () => showNotification({ message: 'Job created successfully', type: 'success' })
    }
  )
}

export const useCreateJobCategoryMutation = (showNotification: Function) => {
  return useMutation<JobCategory, Error, Partial<JobCategory>>(
    async (newJobData: Partial<JobCategory>) => {
      const result = await makeCorePostRequest('/iam/management-tools/job-categories', newJobData)
      return result
    },
    {
      onError: error => handleQueryError(error, 'There was an error creating the job category.', showNotification),
      onSuccess: () => showNotification({ message: 'Job category created successfully', type: 'success' })
    }
  )
}

